import type { DashboardElement } from "../dashboard/catalog"

export const MANUAL_TILE_TYPES = [
  "BarChart",
  "LineChart",
  "AreaChart",
  "PieChart",
  "KPICard",
  "Table",
  "Text",
  "Alert",
  "ProgressBar",
] as const

export type ManualTileType = (typeof MANUAL_TILE_TYPES)[number]
export type TileWidth = "full" | "half"

export const MANUAL_TILE_TYPE_I18N_KEYS: Record<ManualTileType, string> = {
  BarChart: "dash.tile_edit.type_bar_chart",
  LineChart: "dash.tile_edit.type_line_chart",
  AreaChart: "dash.tile_edit.type_area_chart",
  PieChart: "dash.tile_edit.type_pie_chart",
  KPICard: "dash.tile_edit.type_kpi_card",
  Table: "dash.tile_edit.type_table",
  Text: "dash.tile_edit.type_text",
  Alert: "dash.tile_edit.type_alert",
  ProgressBar: "dash.tile_edit.type_progress_bar",
}

export type ManualDataRow = Record<string, string | number | null>

export type ParsedManualData =
  | { success: true; columns: string[]; rows: ManualDataRow[] }
  | { success: false; error: ManualTileValidationError }

export type ManualTileValidationError =
  | "title_required"
  | "content_required"
  | "value_required"
  | "value_invalid"
  | "progress_invalid"
  | "data_empty"
  | "data_invalid"
  | "data_headers"
  | "data_columns"
  | "category_required"
  | "numeric_column_required"

export type ManualTileDraft = {
  type: ManualTileType
  title: string
  width: TileWidth
  rawData: string
  categoryKey: string
  valueKey: string
  value: string
  unit: string
  content: string
  progressMax: string
}

export type ManualTileBuildResult =
  | { success: true; tile: DashboardElement }
  | { success: false; error: ManualTileValidationError }

const CHART_TYPES = new Set<ManualTileType>(["BarChart", "LineChart", "AreaChart", "PieChart"])

export function manualTileUsesTabularData(type: ManualTileType): boolean {
  return CHART_TYPES.has(type) || type === "Table"
}

export function parseManualTileData(raw: string): ParsedManualData {
  const input = raw.trim()
  if (!input) return { success: false, error: "data_empty" }

  if (input.startsWith("[") || input.startsWith("{")) {
    try {
      const parsed: unknown = JSON.parse(input)
      const values = Array.isArray(parsed) ? parsed : [parsed]
      if (values.length === 0) return { success: false, error: "data_empty" }
      if (values.some((value) => !isPlainObject(value))) {
        return { success: false, error: "data_invalid" }
      }

      const columns = Array.from(
        new Set(values.flatMap((value) => Object.keys(value as Record<string, unknown>)))
      )
      if (columns.length === 0) return { success: false, error: "data_headers" }
      const rows = values.map((value) =>
        Object.fromEntries(
          columns.map((column) => [
            column,
            normaliseManualCell((value as Record<string, unknown>)[column]),
          ])
        )
      ) as ManualDataRow[]
      return { success: true, columns, rows }
    } catch {
      return { success: false, error: "data_invalid" }
    }
  }

  const lines = input
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim().length > 0)
  if (lines.length < 2) return { success: false, error: "data_empty" }

  const delimiter = detectDelimiter(lines[0])
  const columns = (delimiter ? splitDelimitedLine(lines[0], delimiter) : [lines[0]]).map((value) =>
    value.trim()
  )
  if (columns.some((column) => !column) || new Set(columns).size !== columns.length) {
    return { success: false, error: "data_headers" }
  }

  const rows = lines.slice(1).map((line) => {
    const values = delimiter ? splitDelimitedLine(line, delimiter) : [line]
    return Object.fromEntries(
      columns.map((column, index) => [column, normaliseManualCell(values[index] ?? "")])
    )
  }) as ManualDataRow[]

  return rows.length > 0
    ? { success: true, columns, rows }
    : { success: false, error: "data_empty" }
}

export function buildManualTile(draft: ManualTileDraft): ManualTileBuildResult {
  const title = draft.title.trim()
  const withWidth = (tile: Record<string, unknown>): DashboardElement =>
    ({ ...tile, width: draft.width }) as DashboardElement

  if (draft.type === "Text") {
    const content = draft.content.trim()
    return content
      ? { success: true, tile: withWidth({ type: "Text", content, variant: "body" }) }
      : { success: false, error: "content_required" }
  }

  if (draft.type === "Alert") {
    const message = draft.content.trim()
    return message
      ? {
          success: true,
          tile: withWidth({ type: "Alert", title: title || undefined, message, variant: "info" }),
        }
      : { success: false, error: "content_required" }
  }

  if (!title) return { success: false, error: "title_required" }

  if (draft.type === "KPICard") {
    const rawValue = draft.value.trim()
    if (!rawValue) return { success: false, error: "value_required" }
    return {
      success: true,
      tile: withWidth({
        type: "KPICard",
        title,
        value: coerceNumber(rawValue) ?? rawValue,
        unit: draft.unit.trim() || undefined,
      }),
    }
  }

  if (draft.type === "ProgressBar") {
    const value = coerceNumber(draft.value)
    const max = coerceNumber(draft.progressMax)
    if (value === null || max === null || max <= 0 || value < 0 || value > max) {
      return { success: false, error: "progress_invalid" }
    }
    return {
      success: true,
      tile: withWidth({ type: "ProgressBar", label: title, value, max }),
    }
  }

  const parsed = parseManualTileData(draft.rawData)
  if (!parsed.success) return parsed

  if (draft.type === "Table") {
    return {
      success: true,
      tile: withWidth({
        type: "Table",
        title,
        columns: parsed.columns.map((key) => ({ key, label: key })),
        rows: parsed.rows,
      }),
    }
  }

  const categoryKey = draft.categoryKey.trim()
  const valueKey = draft.valueKey.trim()
  if (parsed.columns.length < 2) return { success: false, error: "data_columns" }
  if (!categoryKey || !parsed.columns.includes(categoryKey)) {
    return { success: false, error: "category_required" }
  }
  if (!valueKey || valueKey === categoryKey || !parsed.columns.includes(valueKey)) {
    return { success: false, error: "numeric_column_required" }
  }
  const numericRows = parsed.rows.map((row) => coerceNumber(row[valueKey]))
  if (numericRows.some((value) => value === null)) {
    return { success: false, error: "numeric_column_required" }
  }

  const data = parsed.rows.map((row, index) => ({ ...row, [valueKey]: numericRows[index]! }))
  const series = { dataKey: valueKey, label: valueKey, color: "hsl(var(--chart-1))" }

  if (draft.type === "PieChart") {
    return {
      success: true,
      tile: withWidth({
        type: "PieChart",
        title,
        data: data.map((row, index) => ({
          name: String(row[categoryKey] ?? ""),
          value: Number(row[valueKey]),
          color: `hsl(var(--chart-${(index % 5) + 1}))`,
        })),
      }),
    }
  }

  if (draft.type === "LineChart") {
    return {
      success: true,
      tile: withWidth({ type: "LineChart", title, data, xKey: categoryKey, lines: [series] }),
    }
  }
  if (draft.type === "AreaChart") {
    return {
      success: true,
      tile: withWidth({ type: "AreaChart", title, data, xKey: categoryKey, areas: [series] }),
    }
  }
  return {
    success: true,
    tile: withWidth({
      type: "BarChart",
      title,
      data,
      xKey: categoryKey,
      bars: [series],
      layout: "horizontal",
    }),
  }
}

export function insertManualTile(
  children: DashboardElement[],
  tile: DashboardElement,
  insertionIndex: number
): DashboardElement[] {
  const next = [...children]
  const safeIndex = Math.max(0, Math.min(insertionIndex, next.length))
  next.splice(safeIndex, 0, tile)
  return next
}

export function getTopLevelTileSpanClass(element: DashboardElement): string {
  return (element as DashboardElement & { width?: TileWidth }).width === "half"
    ? "md:col-span-1"
    : "md:col-span-2"
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value))
}

function detectDelimiter(header: string): "\t" | ";" | "," | null {
  if (header.includes("\t")) return "\t"
  if (header.includes(";")) return ";"
  if (header.includes(",")) return ","
  return null
}

function splitDelimitedLine(line: string, delimiter: string): string[] {
  const values: string[] = []
  let value = ""
  let quoted = false
  for (let index = 0; index < line.length; index++) {
    const char = line[index]
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        value += '"'
        index++
      } else {
        quoted = !quoted
      }
    } else if (char === delimiter && !quoted) {
      values.push(value)
      value = ""
    } else {
      value += char
    }
  }
  values.push(value)
  return values
}

function normaliseManualCell(value: unknown): string | number | null {
  if (value === null || value === undefined || value === "") return null
  if (typeof value === "number") return Number.isFinite(value) ? value : String(value)
  if (typeof value !== "string") return JSON.stringify(value)
  const trimmed = value.trim()
  if (!trimmed) return null
  return coerceNumber(trimmed) ?? trimmed
}

function coerceNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  if (typeof value !== "string") return null
  const compact = value.trim().replace(/[\s\u00a0]/g, "")
  if (!/^-?\d+(?:[.,]\d+)?$/.test(compact)) return null
  const parsed = Number(compact.replace(",", "."))
  return Number.isFinite(parsed) ? parsed : null
}
